/** Landing bosh sahifasi va bo'lim sahifalari uchun umumiy yordamchilar. */
import { useEffect, useState } from "react";
import type { CSSProperties, PointerEvent as ReactPointerEvent } from "react";

import { portalApi } from "@/shared/api/endpoints";
import type { PortalBanner, PublicHome } from "@/shared/api/types";
import { faq as demoFaq } from "@/shared/data/content";
import { useI18n } from "@/shared/i18n";

/** Grid ichida kartochkalar navbatma-navbat chiqishi uchun kechikish. */
export function stagger(index: number, step = 60): CSSProperties {
  return { "--reveal-delay": `${index * step}ms` } as CSSProperties;
}

/**
 * Kursor koordinatasini kartochkaga `--mx`/`--my` sifatida uzatadi — CSS
 * shundan yorug'lik dog'ini chizadi. Hodisa grid'da bir marta o'rnatiladi.
 */
export function trackPointer(event: ReactPointerEvent<HTMLElement>) {
  const card = (event.target as HTMLElement).closest<HTMLElement>("[data-spotlight]");
  if (!card) return;
  const box = card.getBoundingClientRect();
  card.style.setProperty("--mx", `${event.clientX - box.left}px`);
  card.style.setProperty("--my", `${event.clientY - box.top}px`);
}

/** Savol-javob CMS'dan alohida endpoint bilan keladi (`portal.FaqItem`). */
export function usePublicFaq() {
  const { lang } = useI18n();
  const [items, setItems] = useState<typeof demoFaq | null>(null);

  useEffect(() => {
    let cancelled = false;
    portalApi
      .faq(lang)
      .then((data) => {
        if (cancelled || !data?.length) return;
        setItems(
          data.map((item) => ({
            category: item.category,
            question: item.question,
            answer: item.answer,
          })),
        );
      })
      .catch(() => {
        /* backend ulanmagan — zaxira savollar ko'rsatiladi */
      });
    return () => {
      cancelled = true;
    };
  }, [lang]);

  return items ?? demoFaq;
}

/**
 * Hero fon rasm(lar)i — `portal.HeroBackground` (superadmin panelida
 * boshqariladi). Asosiy fon — 3D globus (`HeroGlobe`); rasm uning zaxirasi.
 */
export function HeroBackground({ home }: { home: PublicHome | null }) {
  const images = (home?.hero_backgrounds ?? [])
    .filter((item) => item.is_active && item.image_link)
    .map((item) => item.image_link);

  if (images.length <= 1) {
    const style = images[0] ? ({ backgroundImage: `url(${images[0]})` } as CSSProperties) : undefined;
    return <span className="hero-bg hero-bg-static" aria-hidden="true" style={style} />;
  }

  const loop = [...images, ...images];
  const duration = `${images.length * 9}s`;
  return (
    <div className="hero-bg hero-bg-scroll" aria-hidden="true">
      <div className="hero-bg-track" style={{ animationDuration: duration } as CSSProperties}>
        {loop.map((url, index) => (
          <span
            key={index}
            className="hero-bg-frame"
            style={{ backgroundImage: `url(${url})` } as CSSProperties}
          />
        ))}
      </div>
    </div>
  );
}

/** Ogohlantirish banneri (yong'in xavfi) — `Banner.kind = "alert"`. */
export function pickAlert(home: PublicHome | null): PortalBanner | null {
  return home?.banners?.find((banner) => banner.kind === "alert") ?? null;
}
